import { computed, ref } from 'vue'

import { session as globalSession } from '@/app/session'
import { useI18n } from '@/shared/composables/useI18n'
import { useConfirm } from '@/shared/ports'

import { buildRaidCalendarWeek, weekStartsOnFor } from '../lib/raidCalendar'
import type { GroupId, RoomId } from '../types'
import { useBoardState } from './board/state'
import { useMutationRunners } from './board/mutations'
import { useBoardActions } from './board/actions'
import { useOptimisticMoves } from './board/optimistic'
import { useGroupRoomActions } from './board/groupRoom'
import { useWeaponActions } from './board/weapons'
import { usePlayerBuildsActions } from './board/playerBuilds'
import { useMyBuilds } from './board/myBuilds'
import { useFetchBoard } from './board/fetchBoard'
import { useRaidState } from './raid/state'
import { useRaidActions } from './raid/actions'
import { useRaidLifecycleActions } from './raid/lifecycle'
import { useCalendarActions } from './raid/calendar'
import { useStageActions } from './raid/stages'
import { useFetchRaidBoard } from './raid/fetchRaidBoard'
import { useRaidMoves } from './raid/moves'
import { useRaidRsvpActions } from './raid/rsvp'
import { useTagState } from './tags/state'
import { useTagActions } from './tags/actions'
import { useTagAssignmentActions } from './tags/assignments'
import { useTagResetActions } from './tags/reset'
import { useSessionState, useSessionActions } from './session'
import { useNotificationStoreComposition } from './notificationStoreComposition'
import { useBuildPresetState } from './buildPresets/state'
import { useBuildPresetActions } from './buildPresets/actions'

export function useGvgPlannerStoreCore() {
  // ── state (lives in sub-modules) ────────────────────────────────────────
  const board = useBoardState()
  const raid = useRaidState()
  const tag = useTagState()
  const buildPreset = useBuildPresetState()
  const session = useSessionState()
  const notificationSettings = useNotificationStoreComposition()

  // ── shared store refs ──────────────────────────────────────────────────
  const loading = ref<boolean>(true)
  const error = ref<string | null>(null)
  const liveSyncError = ref<string | null>(null)
  /**
   * Handle for the live `boardChanged` subscription, or `null` when none is
   * active. Reassigned every time `dbGuildId` changes so each active guild
   * gets exactly one subscription; the previous one is unsubscribed first.
   */
  const subscription = ref<{ unsubscribe(): void } | null>(null)
  /**
   * Generation counter for initial/recovery `fetchBoard` runs. A later
   * request wins over an older in-flight result so initial navigation cannot
   * replace a newer authoritative snapshot.
   */
  const fetchGeneration = ref<number>(0)
  /**
   * Every complete subscription delivery advances this counter. Selection
   * reads capture it so an older `RaidBoard` or stage query cannot overwrite
   * an event snapshot that arrived while the request was in flight.
   */
  const liveSnapshotGeneration = ref<number>(0)
  /**
   * Numeric-id lookup maps — populated by `fetchBoard` so mutations can
   * resolve the frontend's stable ids back to the backend's DB PKs.
   */
  const numericRoomId = new Map<string, number>()
  const numericGroupId = new Map<string, number>()
  const numericRaidId = new Map<string, number>()
  /**
   * Raid-mode equivalents of `numericRoomId` / `numericGroupId`. They
   * key on `stableId` (the FE-stable per-row id the render layer hands
   * around — `"group-5"`, `"pool"`, `"room-25"`) and resolve to the
   * backend `raid_groups.id` / `raid_rooms.id` PKs the GraphQL
   * mutations expect. They are derived directly from
   * `raid.raidRooms.value` / `raid.raidGroups.value` — every authoritative
   * snapshot delivery replaces them, so they stay in lockstep with
   * `currentRooms` / `currentGroups`.
   */
  const raidNumericRoomId = computed<ReadonlyMap<string, number>>(() =>
    new Map(
      raid.raidRooms.value.map((rr) => [rr.stableId, Number(rr.id)]),
    ),
  )
  const raidNumericGroupId = computed<ReadonlyMap<string, number>>(() =>
    new Map(
      raid.raidGroups.value.map((rg) => [rg.stableId, Number(rg.id)]),
    ),
  )

  // Forward declarations — defined after composing sub-modules.
  let fetchBoardFn: (afterInFlight?: boolean) => Promise<void> = async () => {}
  let fetchRaidBoardFn: (id: string, shouldApply?: () => boolean) => Promise<boolean> = async () => false

  // Application locale feeds the calendar week-math. The locale is the
  // host-owned `localePort.locale` computed (from `@/shared/composables/useI18n`)
  // — every `ComputedRef<AppLocale>` readout re-evaluates `calendarWeek`
  // and refetches the GraphQL window when the user flips their language.
  const { locale } = useI18n()

  // ── session-actions (declared before tag-actions so it can resolve) ──
  const calendarWeekComputed = computed(() =>
    buildRaidCalendarWeek(
      raid.calendarWindowStart.value,
      raid.raidsForWindow.value,
      weekStartsOnFor(locale.value),
    ),
  )

  const calendarActions = useCalendarActions({
    activeDiscordGuildId: session.activeDiscordGuildId,
    error,
    calendarWindowStart: raid.calendarWindowStart,
    raidsForWindow: raid.raidsForWindow,
    locale,
  })

  // `confirmPort` is captured at setup-time so the same reference is
  // threaded into `useRaidActions` for the store's lifetime — the
  // destructive-action prompt is host-owned (wired to `window.confirm`
  // in `main.ts`) and the store never reaches for the browser API
  // directly (Phase 3 — CONFIRM_PORT seam).
  const confirmPort = useConfirm()

  const runners = useMutationRunners({ error })

  function gid(id: GroupId): number {
    return numericGroupId.get(id) ?? Number(id)
  }
  function rid(id: RoomId): number {
    return numericRoomId.get(id) ?? Number(id)
  }

  // `useFetchBoard` runs a single-flight initial/recovery fetch that
  // orchestrates the player/group/room reconstruction. Live subscription
  // deliveries use `applyPlannerState` below and never call this query.
  const fetchBoardApi = useFetchBoard({
    activeDiscordGuildId: session.activeDiscordGuildId,
    error,
    loading,
    players: board.players,
    guildPlayerDiscordIds: board.guildPlayerDiscordIds,
    groups: board.groups,
    rooms: board.rooms,
    fetchGeneration,
    numericRoomId,
    numericGroupId,
    playerBuildPresetOwners: buildPreset.playerBuildPresetOwners,
    guildBuildPresets: buildPreset.guildBuildPresets,
    clearBuildPresetLibrary: buildPreset.clearLibrary,
    resolveDbGuildId: async () => (await sessionActions.resolveDbGuildId()),
    fetchPlayerTags: async () => {
      await tagActions.fetchPlayerTags()
    },
  })

  const sessionActions = useSessionActions({
    liveSyncError,
    error,
    subscription,
  })

  const tagActions = useTagActions({
    activeDiscordGuildId: session.activeDiscordGuildId,
    dbGuildId: session.dbGuildId,
    error,
    playerTags: tag.playerTags,
    activeTagFilter: tag.activeTagFilter,
    resolveDbGuildId: sessionActions.resolveDbGuildId,
  })

  // ── tag-assignment composable (assignTag / unassignTag / filter setter) ─
  // Extracted from `tags/actions.ts` in Phase 0 so the catalog CRUD there
  // stays one mutation per action. The MAX_TAGS_PER_PLAYER pre-flight cap
  // moved with `assignTag`; `playerTags` shapers (active filter is/isActive)
  // moved with the setter.
  const tagAssignmentActions = useTagAssignmentActions({
    activeDiscordGuildId: session.activeDiscordGuildId,
    error,
    players: board.players,
    activeRaidId: raid.activeRaidId,
    activeStageId: raid.activeStageId,
    raidPlayers: raid.raidPlayers,
    playerTags: tag.playerTags,
    activeTagFilter: tag.activeTagFilter,
    runReporting: runners.runReporting,
  })

  // ── tag-reset composable (multi-step orchestration) ────────────────────
  // Owns `resetAllTags`. Catalog CRUD lives in `tagActions`; `reset` is
  // a four-mutation chori whose failure modes deserve their own module.
  const tagResetActions = useTagResetActions({
    activeDiscordGuildId: session.activeDiscordGuildId,
    error,
    playerTags: tag.playerTags,
    activeTagFilter: tag.activeTagFilter,
    runReporting: runners.runReporting,
  })

  // ── raid actions — `fetchRaidBoard` closure supplied after the
  //    dedicated composable runs. ────────────────────────────────────────
  const fetchRaidBoardApi = useFetchRaidBoard(
    {
      activeDiscordGuildId: session.activeDiscordGuildId,
      error,
      liveSnapshotGeneration,
      activeStageId: raid.activeStageId,
      raidRooms: raid.raidRooms,
      raidGroups: raid.raidGroups,
      raidPlayers: raid.raidPlayers,
      raidStages: raid.raidStages,
      raidStageDiffs: raid.raidStageDiffs,
    },
    raid.activeRaidBoard,
  )

  // ── raid lifecycle (snapshot selection race guard + rollback) ────────
  // Owns `selectRaid`. Both the public API and the `ctx.selectRaid` slot
  // the rest of the raid surface reads are wired through this single
  // instance so the latestSelection counter + rollback snapshot stay
  // shared across the whole surface (cal → raid → apply / delete).
  const raidLifecycleActions = useRaidLifecycleActions({
    activeRaidId: raid.activeRaidId,
    activeRaidBoard: raid.activeRaidBoard,
    raidRooms: raid.raidRooms,
    raidGroups: raid.raidGroups,
    raidPlayers: raid.raidPlayers,
    activeStageId: raid.activeStageId,
    raidStages: raid.raidStages,
    raidStageDiffs: raid.raidStageDiffs,
    stageSelectionPending: raid.stageSelectionPending,
    loadStagesForRaid: (id, shouldApply) => stageActions.loadStagesForRaid(id, shouldApply),
    fetchRaidBoard: (id, shouldApply) => fetchRaidBoardFn(id, shouldApply),
  })

  const raidActions = useRaidActions({
    activeDiscordGuildId: session.activeDiscordGuildId,
    error,
    activeRaidId: raid.activeRaidId,
    raids: raid.raids,
    raidRooms: raid.raidRooms,
    raidGroups: raid.raidGroups,
    raidPlayers: raid.raidPlayers,
    raidStages: raid.raidStages,
    activeStageId: raid.activeStageId,
    activeRaidBoard: raid.activeRaidBoard,
    calendarWeek: calendarWeekComputed,
    numericRaidId,
    numericRoomId,
    run: runners.run,
    runReporting: runners.runReporting,
    loadStagesForRaid: (id, shouldApply) => stageActions.loadStagesForRaid(id, shouldApply),
    fetchRaidBoard: (id, shouldApply) => fetchRaidBoardFn(id, shouldApply),
    selectRaid: raidLifecycleActions.selectRaid,
    // Captured at setup-time so the reference is stable for the lifetime of
    // the store (Phase 3 — the raid actions are now `window.confirm`-agnostic).
    confirm: (m: string) => confirmPort.confirm(m),
  })

  const stageActions = useStageActions({
    activeDiscordGuildId: session.activeDiscordGuildId,
    error,
    liveSnapshotGeneration,
    raidStages: raid.raidStages,
    activeStageId: raid.activeStageId,
    stageSelectionPending: raid.stageSelectionPending,
    activeRaidId: raid.activeRaidId,
    runReporting: runners.runReporting,
  })

  // Bind forward-declarations now that all sub-modules are wired up.
  fetchBoardFn = fetchBoardApi.fetchBoard
  fetchRaidBoardFn = fetchRaidBoardApi.fetchRaidBoard

  // ── board actions ─────────────────────────────────────────────────────
  const boardActions = useBoardActions({
    activeDiscordGuildId: session.activeDiscordGuildId,
    error,
    players: board.players,
    groups: board.groups,
    rooms: board.rooms,
    viewMode: board.viewMode,
    playerNameMode: board.playerNameMode,
    numericRoomId,
    fetchBoard: () => fetchBoardFn(),
  })

  const groupRoomActions = useGroupRoomActions({
    activeDiscordGuildId: session.activeDiscordGuildId,
    error,
    run: runners.run,
    gid,
  })

  const weaponActions = useWeaponActions({
    activeDiscordGuildId: session.activeDiscordGuildId,
    error,
    players: board.players,
    groups: board.groups,
    run: runners.run,
  })

  const playerBuildsActions = usePlayerBuildsActions({
    activeDiscordGuildId: session.activeDiscordGuildId,
    error,
    players: board.players,
    activeStageId: raid.activeStageId,
    run: runners.run,
  })

  /**
   * Current Discord user id of the signed-in officer, or `'0'` when signed
   * out. Mirrors the `discordUserId` field on the auth payload exposed by
   * `useSession` (`apps/frontend/src/composables/useSession.ts`). The
   * my-builds slice reads this so its `myPlayerBuilds(discordUserId:)`
   * query and player-lookup helper both see the same identity.
   */
  const myDiscordUserId = computed<string>(() => globalSession.value?.discordUserId ?? '0')

  /**
   * Cross-server build library for the signed-in player. Mutations go
   * through the discord-id-keyed `my*Build*` resolvers so the player
   * can edit their library from `/my-builds` even when their player
   * row is on a different guild (or no active board at all) — see
   * `stores/board/myBuilds.ts` for the per-action wiring.
   */
  const myBuildsActions = useMyBuilds({
    discordUserId: myDiscordUserId,
  })
  const buildPresetActions = useBuildPresetActions({
    activeDiscordGuildId: session.activeDiscordGuildId,
    activeRaidId: raid.activeRaidId,
    discordUserId: myDiscordUserId,
    myBuildPresets: buildPreset.myBuildPresets,
    myBuildPresetsLoading: buildPreset.myBuildPresetsLoading,
    myBuildPresetsError: buildPreset.myBuildPresetsError,
    error,
    runReporting: runners.runReporting,
  })

  const optimisticMoves = useOptimisticMoves({
    activeDiscordGuildId: session.activeDiscordGuildId,
    players: board.players,
    groups: board.groups,
    rooms: board.rooms,
    run: runners.run,
    runMutate: runners.runMutate,
    gid,
    rid,
  })

  // Raid-board drag-and-drop intentionally stays non-optimistic. Every
  // commit publishes `raid.updated`; the subscription applies its complete
  // snapshot directly, without a follow-up `RaidBoard` query.
  const raidMoves = useRaidMoves({
    activeDiscordGuildId: session.activeDiscordGuildId,
    error,
    activeRaidId: raid.activeRaidId,
    activeStageId: raid.activeStageId,
    // Pass the computed refs directly (NOT `.value`) — the maps are empty
    // until the first raid-board fetch resolves and rebuild on every
    // subscription delivery. The slice reads `.value` at mutation time so it always sees
    // the latest stable-id → DB-PK lookup. A `.value` snapshot here would
    // freeze the empty map and send `NaN` to the `Int!` GraphQL args.
    raidNumericGroupId,
    raidNumericRoomId,
    runReporting: runners.runReporting,
  })

  const raidRsvpActions = useRaidRsvpActions({
    activeDiscordGuildId: session.activeDiscordGuildId,
    activeRaidId: raid.activeRaidId,
    error,
    runReporting: runners.runReporting,
  })

  // Raid-board build switching. The card's build selector fires `use-build`
  // with a SNAPSHOT row id (`raid_players.id`), which the live
  // `playerSetActiveBuild` mutation cannot target (it writes the live
  // `players` table keyed by the live `players.id`). This slice routes the
  // switch through `raidPlayerReplaceBuilds`, which is snapshot-scoped and
  // re-submits the player's existing builds with only the active index
  // changed. Its authoritative update arrives through the same event path.

  return {
    board,
    raid,
    tag,
    buildPreset,
    session,
    notificationSettings,
    loading,
    error,
    liveSyncError,
    subscription,
    fetchGeneration,
    liveSnapshotGeneration,
    numericRoomId,
    numericGroupId,
    numericRaidId,
    raidNumericRoomId,
    raidNumericGroupId,
    fetchBoardFn,
    fetchRaidBoardFn,
    calendarWeekComputed,
    calendarActions,
    confirmPort,
    runners,
    gid,
    rid,
    fetchBoardApi,
    sessionActions,
    tagActions,
    tagAssignmentActions,
    tagResetActions,
    fetchRaidBoardApi,
    raidLifecycleActions,
    raidActions,
    stageActions,
    boardActions,
    groupRoomActions,
    weaponActions,
    playerBuildsActions,
    myDiscordUserId,
    myBuildsActions,
    buildPresetActions,
    optimisticMoves,
    raidMoves,
    raidRsvpActions,
    locale,
  }
}
